import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeydown);
  }

  /**
   * C27 (2026-10-21): Enter in a cart quantity field applies the quantity,
   * as leaving the field does. It used to submit the cart form, whose first
   * submit button is Checkout, so the customer landed on checkout.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Enter' || !(event.target instanceof HTMLInputElement)) return;
    event.preventDefault();
    this.setQuantity(event);
  };

  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // C27 (review B3b): disabling the focused button (decrease to the
    // minimum, increase to the maximum) silently dropped focus to <body>.
    // Hand it to the line's other button before it is lost (2026-10-21: the
    // quantity input, the earlier target, selects its digits on focus), and
    // to the input only when both buttons are disabled.
    const active = document.activeElement;
    const losingFocus =
      (active === minusButton && value <= min) ||
      (active === plusButton && effectiveMax !== null && value >= effectiveMax);

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    if (losingFocus) {
      const other = active === minusButton ? plusButton : minusButton;
      const target = other && !other.disabled ? other : this.querySelector('input');
      target?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
